import { PagenatedResponse, SearchPagenateQuery } from '@models/common';
import { PaymentStatementSettlementStatus, RefundStatus } from '@models/paymentStatement';
import { ProductOrderViewModel } from '@models/productOrderView';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createSafeStorage, createSizePartialize } from '@utils/localStorage';

const PAGE_SIZE = 20;

interface ProductOrdersContainer {
  contents: ProductOrderViewModel[];
  page: number;
  size: number;
  total: number;
  hasNext: boolean;
}

export interface ProductOrdersViewQuery extends SearchPagenateQuery {
  buyerId?: string;
  paymentStatus?: string;
  refundStatus?: string;
  dateType?: string;
  saleType?: string;
  refundType?: RefundStatus;
  isSettlementTarget?: boolean;
  settlementStatus?: PaymentStatementSettlementStatus;
  channel?: string;
}

interface ProductOrdersStoreProps {
  searchResultContainer: ProductOrdersContainer;
  searchQuery: ProductOrdersViewQuery;
  searchKeyword?: string;
  setStartDate: (date: string | null) => void;
  setEndDate: (date: string | null) => void;
  setSearchKeyword: (keyword: string) => void;
  updateSearchQuery: (query: { [key: string]: any }) => void;
  resetSearchQuery: () => void;
  setProductOrdersContainer: (result: PagenatedResponse<ProductOrderViewModel>) => void;
}

export const useSettlementsStore = create(
  persist<ProductOrdersStoreProps>(
    (set) => ({
      searchResultContainer: {
        contents: [] as ProductOrderViewModel[],
        total: 0,
        page: 0,
        size: 0,
        hasNext: false,
      },
      searchQuery: {
        page: 0,
        size: PAGE_SIZE,
        dateType: 'settlement.transferAt',
        startDate: undefined,
        endDate: undefined,
        isSettlementTarget: false,
        channel: 'all',
      },
      setStartDate: (date: string | null) => {
        set((state) => {
          return { ...state, searchQuery: { ...state.searchQuery, startDate: date } };
        });
      },
      setEndDate: (date: string | null) => {
        set((state) => {
          return { ...state, searchQuery: { ...state.searchQuery, endDate: date } };
        });
      },
      searchKeyword: undefined,
      setProductOrdersContainer: (result) => {
        set((state) => {
          const newValue = {
            ...state.searchResultContainer,
            ...result,
            contents: result?.contents || [],
          };
          return { ...state, searchResultContainer: newValue };
        });
      },
      setSearchKeyword: (keyword: string) => {
        set((state) => {
          return {
            ...state,
            searchKeyword: keyword,
            searchResultContainer: { ...state.searchResultContainer, size: 0 },
          };
        });
      },
      updateSearchQuery: (query: { [key: string]: any }) => {
        set((state) => {
          return {
            ...state,
            searchQuery: { ...state.searchQuery, page: 0, ...query },
            searchResultContainer: { ...state.searchResultContainer, size: 0 },
          };
        });
      },
      resetSearchQuery: () => {
        set({
          searchQuery: {
            page: 0,
            size: PAGE_SIZE,
            dateType: 'settlement.transferAt',
            startDate: undefined,
            endDate: undefined,
            isSettlementTarget: false,
            keyword: '',
            channel: 'all',
          },
        });
      },
    }),
    {
      name: 'igogo-admin-settlements',
      storage: createSafeStorage(),
      partialize: createSizePartialize<ProductOrdersStoreProps>(100),
    },
  ),
);
